import type { ErrorEvent } from "@sentry/browser";
import {
  BrowserClient,
  defaultStackParser,
  makeFetchTransport,
  Scope,
} from "@sentry/browser";

import type { LockerverseEnvironment, LockerverseSdkError } from "./types.ts";

const sentryClients = new Map<string, BrowserClient>();

function stripUrlDetails(value: string | undefined) {
  if (!value) {
    return value;
  }

  try {
    const url = new URL(value);
    url.username = "";
    url.password = "";
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return value.replace(/[?#][^\s)]*/g, "");
  }
}

function sanitizeSentryEvent(event: ErrorEvent) {
  Reflect.deleteProperty(event, "breadcrumbs");
  Reflect.deleteProperty(event, "request");
  Reflect.deleteProperty(event, "user");

  for (const exception of event.exception?.values ?? []) {
    for (const frame of exception.stacktrace?.frames ?? []) {
      const filename = stripUrlDetails(frame.filename);
      if (filename) {
        frame.filename = filename;
      }
    }
  }

  return event;
}

function getSentryClient(
  dsn: string,
  environment: LockerverseEnvironment,
  sdkVersion: string
) {
  const key = `${environment}:${dsn}`;
  const existing = sentryClients.get(key);
  if (existing) {
    return existing;
  }

  const client = new BrowserClient({
    beforeSend: sanitizeSentryEvent,
    dsn,
    environment,
    integrations: [],
    release: `lockerverse-sdk@${sdkVersion}`,
    sendDefaultPii: false,
    stackParser: defaultStackParser,
    transport: makeFetchTransport,
  });
  client.init();
  sentryClients.set(key, client);
  return client;
}

export function captureSdkError({
  context,
  contextName,
  dsn,
  environment,
  error,
}: {
  context: Record<string, boolean | number | string | null>;
  contextName: string;
  dsn: string;
  environment: LockerverseEnvironment;
  error: LockerverseSdkError;
}) {
  try {
    const scope = new Scope();
    scope.setClient(getSentryClient(dsn, environment, error.sdkVersion));
    scope.setTags({
      "sdk.code": error.code,
      "sdk.operation": error.operation,
      "sdk.version": error.sdkVersion,
      source: "lockerverse-sdk",
    });
    if (error.failureKind) {
      scope.setTag("sdk.failure_kind", error.failureKind);
    }
    scope.setContext(contextName, context);
    scope.captureException(error);
  } catch {
    // Telemetry is best-effort and must not replace the checkout error.
  }
}
